import React, { useEffect, useState } from 'react';

import { useStoreBootstrap, useStoreUrl } from '../contexts/StoreBootstrap';

interface MaintenanceStatus {
    maintenance_mode: boolean;
    maintenance_message: string;
}

const MaintenanceMode: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const bootstrap = useStoreBootstrap();
    const requestUrl = useStoreUrl();
    const [loading, setLoading] = useState(!bootstrap);
    const [maintenance, setMaintenance] = useState<MaintenanceStatus | null>(bootstrap?.maintenance || null);

    useEffect(() => {
        checkMaintenanceMode();
    }, []);

    const checkMaintenanceMode = async () => {
        try {
            // Check for emergency bypass in URL
            const urlParams = new URLSearchParams(window.location.search);
            const emergencyBypass = urlParams.get('emergency_disable_maintenance');
            
            // Emergency bypass code (change this to something secret!)
            if (emergencyBypass === 'felgocar_emergency_2026') {
                console.log('🚨 EMERGENCY BYPASS ACTIVATED - Maintenance mode disabled');
                setMaintenance({ maintenance_mode: false, maintenance_message: '' });
                setLoading(false);
                return;
            }

            const res = await fetch('/api/admin/maintenance', { credentials: 'include' });
            if (res.ok) {
                const data: MaintenanceStatus = await res.json();
                setMaintenance(data);
            }
        } catch (error) {
            console.error('Error checking maintenance mode:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-[#0a0e1a] to-[#1a1f2e]">
                <div className="w-12 h-12 border-4 border-cyan-500/20 border-t-cyan-500 rounded-full animate-spin"></div>
            </div>
        );
    }

    if (maintenance?.maintenance_mode) {
        // [MOD] Maintenance is active ONLY for homepage
        const isHomepage = requestUrl.pathname === '/' || requestUrl.pathname === '/index.html';
        
        if (!isHomepage) {
            // Allow access to other pages
            return <>{children}</>;
        }

        const message = maintenance.maintenance_message || 'Przepraszamy, strona jest tymczasowo niedostępna. Prowadzimy prace konserwacyjne. Wrócimy wkrótce!';
        
        return (
            <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-[#0a0e1a] to-[#1a1f2e] text-white p-5">
                <div className="max-w-[600px] text-center bg-[rgba(15,21,34,0.8)] rounded-[20px] border border-cyan-500/20 shadow-[0_25px_50px_rgba(0,0,0,0.5)] p-10 sm:p-[60px_40px]">
                    <div className="text-[80px] mb-[30px] animate-pulse">🔧</div>
                    <h1 className="text-[32px] font-bold mb-5 bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                        Prace konserwacyjne
                    </h1>
                    <p className="text-lg leading-relaxed text-slate-300 mb-[30px]">
                        {message}
                    </p>
                    <div className="w-[50px] h-[50px] my-[30px] mx-auto border-4 border-cyan-500/20 border-t-cyan-500 rounded-full animate-spin"></div>
                    <div className="mt-[30px] pt-[30px] border-t border-cyan-500/20 text-sm text-slate-400">
                        W razie pilnych spraw prosimy o kontakt:<br />
                        <a href="mailto:sklep@felgocar.pl" className="text-cyan-400 hover:underline">
                            sklep@felgocar.pl
                        </a>
                    </div>
                </div>
            </div>
        );
    }

    return <>{children}</>;
};

export default MaintenanceMode;
